import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  useTheme
} from '@mui/material';
import {
  Movie,
  People,
  TrendingUp,
  Star,
  Chat,
  EmojiEvents
} from '@mui/icons-material';

const AboutUs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const features = [
    {
      icon: <Movie />,
      title: "Entertainment Hub",
      description: "Discover trending movies and shows with our unique HypeRating system"
    },
    {
      icon: <People />,
      title: "Social Community", 
      description: "Connect with fellow entertainment enthusiasts and share your thoughts"
    },
    {
      icon: <TrendingUp />,
      title: "HypeRating System",
      description: "Our proprietary algorithm tracks real-time engagement and popularity"
    },
    {
      icon: <Star />,
      title: "Reviews & Ratings",
      description: "Rate content with our unique tomato/popcorn system (-5 to +5)"
    },
    {
      icon: <Chat />,
      title: "Real-time Chat",
      description: "Instant messaging with typing indicators and live updates"
    },
    {
      icon: <EmojiEvents />,
      title: "Achievements",
      description: "Earn badges and climb leaderboards as you engage with content"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          textAlign: 'center',
          mb: { xs: 3, sm: 6 },
          p: { xs: 2, sm: 4 },
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          About HypeCircle
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
          The ultimate social platform for entertainment enthusiasts
        </Typography>
      </Box>

      <Card
        sx={{
          mb: { xs: 3, sm: 6 },
          background: isDark
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Our Mission
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.7 }}>
            HypeCircle revolutionizes how people discover and discuss entertainment content. 
            We combine social media with advanced algorithms that track real-time engagement, 
            helping users find trending content before it goes viral.
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        What Makes Us Special
      </Typography>
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 6 } }}>
        {features.map((feature, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: '100%',
                background: isDark
                  ? 'rgba(0, 0, 0, 0.3)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                      color: isDark ? '#FFD700' : '#3B82F6',
                      mr: 2
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          background: isDark
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Built with Modern Technology
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['React', 'AWS Amplify', 'GraphQL', 'DynamoDB', 'Lambda', 'Material-UI'].map((tech) => (
              <Chip
                key={tech}
                label={tech}
                sx={{
                  bgcolor: isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                  color: isDark ? '#FFD700' : '#3B82F6',
                  fontWeight: 500
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AboutUs;